import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { LuArrowLeft } from 'react-icons/lu';
import { Breadcrumb, StickyBar } from '../ui';
import LobbySearchBox from './LobbySearchBox';

// Shared frame for lobbyist and client profiles: pinned search, breadcrumb, 320px rail + main column.
export const ProfileLayout = ({ crumb, rail, children }) => {
  const { state } = useParams();
  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-5">
      <StickyBar className="flex items-center gap-2 rounded-lg border border-border bg-card p-2.5 shadow-sm">
        <Link
          to={`/${state}/lobby`}
          className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          <LuArrowLeft className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Back to Lobby</span>
        </Link>
        <LobbySearchBox />
      </StickyBar>
      <Breadcrumb items={[{ label: 'Lobby', to: `/${state}/lobby` }, { label: crumb }]} />
      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div className="space-y-5">{rail}</div>
        <div className="min-w-0 space-y-4">{children}</div>
      </div>
    </div>
  );
};

export const Panel = ({ className = '', children }) => (
  <div className={`rounded-lg border border-border bg-card ${className}`}>{children}</div>
);

export const SectionLabel = ({ children }) => (
  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{children}</p>
);

export const DetailRow = ({ icon: Icon, label, children }) => (
  <div className="grid grid-cols-[16px_86px_1fr] items-start gap-x-2 text-sm">
    <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
    <span className="text-muted-foreground">{label}</span>
    <span className="min-w-0 break-words text-foreground">{children}</span>
  </div>
);

export const MissingState = ({ title, message }) => {
  const { state } = useParams();
  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex flex-col items-center rounded-lg border border-dashed border-border bg-card px-6 py-12 text-center">
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">{message}</p>
        <Link
          to={`/${state}/lobby`}
          className="mt-6 inline-flex h-10 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Back to Lobby
        </Link>
      </div>
    </div>
  );
};

// Segmented year filter used above engagement tables.
export const YearTabs = ({ years, value, onChange }) =>
  years.length > 1 ? (
    <div className="inline-flex h-9 rounded-md bg-muted p-1 text-muted-foreground" role="tablist" aria-label="Year">
      {[...years, 'all'].map((y) => (
        <button
          key={y}
          type="button"
          role="tab"
          aria-selected={value === y}
          onClick={() => onChange(y)}
          className={`rounded-sm px-3 text-sm font-medium transition-colors ${
            value === y ? 'bg-background text-foreground shadow-sm' : 'hover:text-foreground'
          }`}
        >
          {y === 'all' ? 'All years' : y}
        </button>
      ))}
    </div>
  ) : null;
